import { useEffect, useState } from 'react'
import { usePolicies, useSavePolicies } from '../../api/queries'
import type { Policy } from '../../api/types'
import { ErrorNotice } from '../../components/ErrorNotice'
import { Button, Field, Input, Loading, PageTitle, Switch } from '../../components/ui'
import { fmtDuration } from '../../lib/time'

/**
 * Экран 5.3 «Правила».
 *
 * Правила — данные, а не код: меняются здесь и применяются сразу, выкатка не нужна.
 * Уже созданные брони не пересматриваются: сокращение горизонта не отменяет то,
 * что забронировано раньше, — иначе изменение настройки задним числом ломает людям
 * планы, о которых администратор даже не знал.
 */
export function AdminPoliciesPage() {
  const { data, isLoading, error } = usePolicies()
  const save = useSavePolicies()

  const [draft, setDraft] = useState<Policy | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data) setDraft(data)
  }, [data])

  if (isLoading) return <Loading />
  if (error) return <ErrorNotice error={error} />
  if (!draft) return null

  function patch(changes: Partial<Policy>) {
    setDraft((previous) => (previous ? { ...previous, ...changes } : previous))
    setSaved(false)
  }

  return (
    <>
      <PageTitle>Правила бронирования</PageTitle>

      <form
        className="flex max-w-2xl flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault()
          if (draft) save.mutate(draft, { onSuccess: () => setSaved(true) })
        }}
      >
        <div className="card grid gap-4 p-4 sm:grid-cols-2">
          <Field
            label="Горизонт бронирования, дней"
            hint="На сколько дней вперёд можно бронировать. Уже созданные брони не пересматриваются."
          >
            <Input
              type="number"
              min={1}
              className="tnum"
              value={draft.horizon_days}
              onChange={(event) => patch({ horizon_days: Number(event.target.value) })}
            />
          </Field>

          <Field
            label="Максимальная длительность встречи, минут"
            hint={`Сейчас это ${fmtDuration(draft.max_duration_minutes)}.`}
          >
            <Input
              type="number"
              min={30}
              step={30}
              className="tnum"
              value={draft.max_duration_minutes}
              onChange={(event) => patch({ max_duration_minutes: Number(event.target.value) })}
            />
          </Field>

          <Field label="Начало рабочего дня">
            <Input
              type="time"
              step={1800}
              className="tnum"
              value={draft.work_start}
              onChange={(event) => patch({ work_start: event.target.value })}
            />
          </Field>

          <Field
            label="Конец рабочего дня"
            hint="Это время на стене офиса: при смене сезона девять утра остаётся девятью утра."
          >
            <Input
              type="time"
              step={1800}
              className="tnum"
              value={draft.work_end}
              onChange={(event) => patch({ work_end: event.target.value })}
            />
          </Field>
        </div>

        <div className="card flex flex-col gap-4 p-4">
          <ToggleRow
            checked={draft.allow_kiosk_booking}
            onChange={(next) => patch({ allow_kiosk_booking: next })}
            title="Разрешить бронировать с планшета"
            hint="Тот самый сценарий «свободна ещё 15 минут»: человек идёт мимо и занимает комнату кнопкой на двери. Такая бронь без темы и без организатора."
          />
          <ToggleRow
            checked={draft.admin_only_cancel_others}
            onChange={(next) => patch({ admin_only_cancel_others: next })}
            title="Чужую бронь снимает только администратор"
            hint="И только с указанием причины — она уходит организатору и участникам в письме."
          />
        </div>

        {save.error && <ErrorNotice error={save.error} />}

        <div className="flex items-center gap-3">
          <Button type="submit" variant="primary" loading={save.isPending}>
            Сохранить правила
          </Button>
          {saved && !save.isPending && (
            <span className="text-sm text-state-free">Сохранено и уже действует</span>
          )}
        </div>
      </form>
    </>
  )
}

function ToggleRow({
  checked,
  onChange,
  title,
  hint,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  title: string
  hint: string
}) {
  return (
    <div className="flex items-start gap-3">
      <Switch checked={checked} onChange={onChange} label={title} />
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="hint">{hint}</p>
      </div>
    </div>
  )
}
